import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import SongPanel from '../components/SongPanel';

// Define the structure of a Song object
interface Song {
    title: string;
    singer: string;
    team: number;
}

interface SongListType {
    [key: string]: Song;
}

// --- CheckboxGroup Component ---
function CheckboxGroup({ 
    label, 
    options, 
    selected, 
    onChange 
}: { 
    label: string, 
    options: string[], 
    selected: string[], 
    onChange: (selected: string[]) => void 
}) {
    const isAllSelected = selected.length === 0;

    const handleOptionChange = (option: string) => {
        if (selected.includes(option)) {
            onChange(selected.filter(item => item !== option));
        } else {
            onChange([...selected, option]);
        }
    };

    return (
        <div className="flex flex-col mb-2 items-start w-full">
            <span className="text-sm font-bold text-gray-700 mb-1">{label}:</span>
            <div className="flex flex-wrap gap-2">
                <label className={`inline-flex items-center border rounded px-2 py-1 cursor-pointer text-xs ${isAllSelected ? 'bg-blue-100 border-blue-300 text-blue-800' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                    <input
                        type="checkbox"
                        className="hidden"
                        checked={isAllSelected}
                        onChange={() => onChange([])}
                    />
                    <span className={isAllSelected ? "font-bold" : ""}>All</span>
                </label>
                {options.map(option => {
                     const isSelected = selected.includes(option);
                     return (
                        <label key={option} className={`inline-flex items-center border rounded px-2 py-1 cursor-pointer text-xs ${isSelected ? 'bg-blue-100 border-blue-300 text-blue-800' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                            <input
                                type="checkbox"
                                className="hidden"
                                checked={isSelected}
                                onChange={() => handleOptionChange(option)}
                            />
                            <span className={isSelected ? "font-bold" : ""}>
                                {label === 'Team' ? `Team ${option}` : option}
                            </span>
                        </label>
                    );
                })}
            </div>
        </div>
    );
}

function Songs() {
    const [songList, setSongList] = useState<SongListType>({});
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
    const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
    const [selectedSingers, setSelectedSingers] = useState<string[]>([]);
    const [sortOrder, setSortOrder] = useState<'default' | 'asc' | 'desc'>('default');
    const [searchQuery, setSearchQuery] = useState<string>('');

    useEffect(() => {
        fetch(process.env.PUBLIC_URL + '/charts/SongData.json')
            .then(res => res.json())
            .then(setSongList)
            .catch((err) => console.error("Failed to load song data", err));
    }, []);

    const { teams, singers } = useMemo(() => {
        const uniqueTeams = new Set<string>();
        const uniqueSingers = new Set<string>();
        Object.values(songList).forEach(song => {
            uniqueTeams.add(song.team.toString());
            if (song.singer) {
                const splitSingers = song.singer.split('、');
                splitSingers.forEach(s => uniqueSingers.add(s.trim()));
            }
        });
        return { 
            teams: Array.from(uniqueTeams).sort((a, b) => Number(a) - Number(b)), 
            singers: Array.from(uniqueSingers).sort() 
        };
    }, [songList]);

    // Filter Logic
    const filteredSongs = useMemo(() => {
        return Object.entries(songList).filter(([id, song]) => {
            // Team Filter
            const teamMatch = selectedTeams.length === 0 || selectedTeams.includes(song.team.toString());
            
            // Singer Filter
            let singerMatch = selectedSingers.length === 0;
            if (selectedSingers.length > 0 && song.singer) {
                const songSingers = song.singer.split('、').map(s => s.trim());
                singerMatch = songSingers.some(s => selectedSingers.includes(s));
            }

            // Search Filter (Title)
            const searchMatch = song.title.toLowerCase().includes(searchQuery.toLowerCase());

            return teamMatch && singerMatch && searchMatch;
        });
    }, [songList, selectedTeams, selectedSingers, searchQuery]);

    // Sort Logic
    const sortedSongs = useMemo(() => {
        if (sortOrder === 'default') return filteredSongs;

        return [...filteredSongs].sort((a, b) => {
            const titleA = a[1].title.toLowerCase();
            const titleB = b[1].title.toLowerCase();
            if (titleA < titleB) return sortOrder === 'asc' ? -1 : 1;
            if (titleA > titleB) return sortOrder === 'asc' ? 1 : -1;
            return 0;
        });
    }, [filteredSongs, sortOrder]);

    const handleSortClick = () => {
        setSortOrder(prev => {
            if (prev === 'default') return 'asc';
            if (prev === 'asc') return 'desc';
            return 'default';
        });
    };

    const getSortIcon = () => {
        if (sortOrder === 'asc') return ' ▲';
        if (sortOrder === 'desc') return ' ▼';
        return ''; 
    };

    return (
        <div className="flex flex-col items-center w-full min-h-screen bg-gray-100">
            {/* Filter & View Controls */}
            <div className="flex flex-wrap justify-center gap-4 my-4 p-4 bg-white rounded shadow-sm w-11/12 z-10 relative">
                {/* View Toggle */}
                <div className="flex space-x-2 mr-4 h-fit">
                    <button className={`px-3 py-1 rounded text-sm ${viewMode === 'grid' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`} onClick={() => setViewMode('grid')}>Grid</button>
                    <button className={`px-3 py-1 rounded text-sm ${viewMode === 'list' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`} onClick={() => setViewMode('list')}>List</button>
                </div>
                
                {/* Filters */}
                <div className="flex flex-col space-y-4 flex-grow">
                    <CheckboxGroup label="Teams" options={teams} selected={selectedTeams} onChange={setSelectedTeams} />
                    <CheckboxGroup label="Singers" options={singers} selected={selectedSingers} onChange={setSelectedSingers} />
                    
                    {/* Text Search Input */}
                    <div className="flex flex-col items-start w-full">
                         <span className="text-sm font-bold text-gray-700 mb-1">Search Title:</span>
                         <input 
                            type="text" 
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Enter song title..."
                            className="w-full max-w-md px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                         />
                    </div>
                </div>
            </div>
            
            {/* Content Area */}
            {sortedSongs.length === 0 ? (
                <div className="text-gray-500 text-lg mt-10">No song matches the criteria.</div>
            ) : (
                viewMode === 'grid' ? (
                    <div className='flex flex-wrap w-11/12 justify-center'>
                        {sortedSongs.map(([id]) => <SongPanel key={'Panel_' + id} id={id} />)}
                    </div>
                ) : (
                    /* List View */
                    <div className="w-11/12 bg-white shadow rounded overflow-hidden overflow-x-auto mb-10">
                        <table className="min-w-full leading-normal">
                            <thead>
                                <tr>
                                    <th className="px-5 py-3 border-b-2 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase">Cover</th>
                                    <th className="px-5 py-3 border-b-2 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-200 select-none" onClick={handleSortClick}>
                                        Title {getSortIcon()}
                                    </th>
                                    <th className="px-5 py-3 border-b-2 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase">Team</th>
                                    <th className="px-5 py-3 border-b-2 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase">Singer</th>
                                    <th className="px-5 py-3 border-b-2 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase">Levels</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedSongs.map(([id, song]) => (
                                    <tr key={id} className="hover:bg-gray-50">
                                        <td className="px-5 py-2 border-b text-sm text-left">
                                            <div className="w-16 h-16">
                                                <img src={`${process.env.PUBLIC_URL}/images/jacket/jacket_${id}.png`} className="w-full h-full rounded object-cover" alt={song.title} onError={(e: any) => e.target.src = 'https://via.placeholder.com/64?text=No+Img'} />
                                            </div>
                                        </td>
                                        <td className="px-5 py-2 border-b text-sm font-semibold text-left">{song.title}</td>
                                        <td className="px-5 py-2 border-b text-sm text-left">
                                            <div className="w-8 h-8">
                                                 <img src={`${process.env.PUBLIC_URL}/images/teams/team_${song.team}.png`} className="w-full h-full object-contain" alt={`Team ${song.team}`} onError={(e: any) => e.target.style.display = 'none'} />
                                            </div>
                                        </td>
                                        <td className="px-5 py-2 border-b text-sm text-gray-600 text-left">{song.singer}</td>
                                        <td className="px-5 py-2 border-b text-sm text-left">
                                            <div className="flex space-x-2">
                                                <Link to={`/Chart?id=${id}&level=1`} className="bg-yellow-300 text-yellow-900 px-2 py-1 rounded text-xs font-bold hover:bg-yellow-400">N</Link>
                                                <Link to={`/Chart?id=${id}&level=2`} className="bg-orange-300 text-orange-900 px-2 py-1 rounded text-xs font-bold hover:bg-orange-400">H</Link>
                                                <Link to={`/Chart?id=${id}&level=3`} className="bg-red-300 text-red-900 px-2 py-1 rounded text-xs font-bold hover:bg-red-400">A</Link>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )
            )}
        </div>
    );
}

export default Songs;